import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Search, SlidersHorizontal } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const FILTERS = [
  { key: "all", label: "All" }, { key: "veg", label: "Veg" }, { key: "nonveg", label: "Non-Veg" },
  { key: "best", label: "Best Sellers" },
];

export default function Menu() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const category = params.get("category") || "all";
  const dietFilter = params.get("filter") || "all";
  const sort = params.get("sort") || "recommended";

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { setSearch(params.get("search") || ""); }, [params.get("search")]);

  useEffect(() => {
    setLoading(true);
    const p = { sort };
    if (category !== "all") p.category = category;
    if (dietFilter === "veg") p.veg = "veg";
    if (dietFilter === "nonveg") p.veg = "nonveg";
    if (dietFilter === "best") p.best_seller = true;
    if (search) p.search = search;
    const t = setTimeout(() => {
      api.get("/products", { params: p }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [category, dietFilter, sort, search]);

  const setParam = (k, v) => {
    const np = new URLSearchParams(params);
    if (!v || v === "all") np.delete(k); else np.set(k, v);
    setParams(np);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="menu-page">
      <div className="mb-6">
        <div className="overline text-gold mb-2">The complete menu</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy">Digital Menu</h1>
      </div>

      {/* Search + sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex items-center bg-white border border-black/10 rounded-full px-4 py-2.5 flex-1 focus-within:ring-2 focus-within:ring-gold">
          <Search size={18} className="text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by dish name..."
            data-testid="menu-search-input" className="bg-transparent outline-none text-sm ml-2.5 flex-1" />
        </div>
        <div className="flex items-center gap-2 bg-white border border-black/10 rounded-full px-3 py-1">
          <SlidersHorizontal size={16} className="text-muted-foreground" />
          <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger className="border-0 shadow-none w-[190px] focus:ring-0" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recommended">Recommended</SelectItem>
              <SelectItem value="popular">Popular</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Diet filters */}
      <div className="flex gap-2 mb-3 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setParam("filter", f.key)} data-testid={`filter-${f.key}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${dietFilter === f.key ? "bg-navy text-cream" : "bg-white border border-black/10 text-navy hover:border-gold"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Category pills (sticky) */}
      <div className="sticky top-16 lg:top-20 z-30 -mx-4 px-4 py-3 bg-cream/90 backdrop-blur-md border-b border-black/5 mb-6">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          <button onClick={() => setParam("category", "all")} data-testid="cat-all"
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${category === "all" ? "bg-gold text-navy" : "bg-white border border-black/10 text-navy hover:border-gold"}`}>All</button>
          {cats.map((c) => (
            <button key={c.key} onClick={() => setParam("category", c.key)} data-testid={`cat-${c.key}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${category === c.key ? "bg-gold text-navy" : "bg-white border border-black/10 text-navy hover:border-gold"}`}>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="bg-white rounded-xl h-72 animate-pulse border border-black/5" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="menu-empty">No dishes found. Try a different search or filter.</div>
      ) : (
        <>
          <div className="text-sm text-muted-foreground mb-4" data-testid="menu-result-count">{products.length} dishes</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </>
      )}
    </div>
  );
}
